import { useState, KeyboardEvent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginFailedResponse, LoginResponse } from '@/types';
import { loginAPI } from '@/api/login';
import { encodeToken } from '@/utils/tokenUtils';
import BaseCard from '@/components/ui/BaseCard';
import BaseButton from '@/components/ui/BaseButton';
import BaseTextField from '@/components/form/BaseTextField';
import BaseAlert from '@/components/feedback/BaseAlert';
import {
    AccountCircle,
    Lock,
    HowToReg,
    PersonAddAlt1,
} from '@mui/icons-material';
import { showLoading, hideLoading } from '@/components/ui/LoadingOverlay';
import LoadingOverlay from '@/components/ui/LoadingOverlay';
import '@/styles/pages/Login/LoginPage.scss';
import { useAppContext } from '@/provider/contexts';
import logo from '@/assets/images/AURA_logo.png';
import localforage from 'localforage';
import BaseDialog from '@/components/ui/BaseDialog';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SyncSessionService } from '@/services/syncSessionService';
import { getDefaultRedirectPath } from '@/utils/deviceUtils';

export default function LoginPage() {
    const { t } = useTranslation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showAlert, setShowAlert] = useState(false);
    const [disableUI, setDisableUI] = useState(false);
    const [toggleResetDialog, setToggleResetDialog] = useState(false);
    const navigate = useNavigate();
    const { setNotification, setUserDetails } = useAppContext();
    const location = useLocation();

    const loginHandler = async () => {
        try {
            setShowAlert(false);
            setDisableUI(true);
            showLoading();
            setErrorMessage(null);
            setSuccessMessage(null);

            const {
                data: { isSuccess, status, data },
            } = await loginAPI({ login: username, password });

            if (isSuccess) {
                const loginData = data as LoginResponse;

                if (loginData.token_expires) {
                    localStorage.setItem('tokenExpires', loginData.token_expires.toString());
                }
                const loginDataWithoutToken = { ...loginData };
                delete (loginDataWithoutToken as Partial<LoginResponse>).token;
                await localforage.setItem('login_token', encodeToken(loginData.token));
                await localforage.setItem('hasLoginSession', true);
                await localforage.setItem('login_data', loginDataWithoutToken);
                setUserDetails(loginData);

                localStorage.setItem('userDetails', JSON.stringify(loginData));
                localStorage.setItem('isUserLogin', 'isUserLogin');

                setSuccessMessage(t('Login successful!'));
                setShowAlert(true);

                setNotification({ message: t('Login successful!'), severity: 'success' });

                if (!loginData) return;
                SyncSessionService.postMessage({
                    type: 'login',
                    data: loginData,
                });
                const params = new URLSearchParams(location.search);
                const redirectTo = params.get('redirectTo') ?? getDefaultRedirectPath();

                setTimeout(() => {
                    navigate(`/corporation-selection?redirectTo=${encodeURIComponent(redirectTo)}`, { replace: true });
                }, 2000);

                return;
            }
            if (status === 400) {
                const loginData = data as LoginFailedResponse;
                if (loginData?.reason === 'expired_password') {
                    setToggleResetDialog(true);
                    await localforage.setItem('recovery_token', encodeToken(loginData?.recovery_token));
                    return;
                }
                throw new Error();
            }

            throw new Error();
        } catch {
            setErrorMessage(t('Login failed. Please check your credentials.'));
            setShowAlert(true);
            setNotification({ message: t('Login failed. Please check your credentials.'), severity: 'error' });
        } finally {
            hideLoading();
            setDisableUI(false);
        }
    };

    const enterKeyHandler = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            loginHandler();
        }
    };

    // Force change password for 90days
    useEffect(() => {
        const message = localStorage.getItem('reset_message');
        if (message) {
            setErrorMessage(message);
            setShowAlert(true);
            setNotification({
                message,
                severity: 'error',
            });
            localStorage.removeItem('reset_message');
        }
    }, []);

    useEffect(() => {
        const msg = localStorage.getItem('autoLogoutMessage');
        if (msg) {
            setNotification({ message: msg, severity: 'error' });
            localStorage.removeItem('autoLogoutMessage');
        }
    }, [setNotification]);
    return (
        <div className="login-page">
            <LoadingOverlay />
            {disableUI && <div className="blocking-overlay"></div>}

            <BaseCard className="login-container">
                <div className="login-logo-container">
                    <img src={logo} alt={t('AURA Logo')} className="login-logo" />
                </div>

                <div className="login-header">
                    <span className="line"></span>
                    <h3 className="login-signin">{t('SIGN IN')}</h3>
                    <span className="line"></span>
                </div>

                <div className="login-form">
                    <BaseTextField
                        placeholder={t('Username')}
                        startIcon={<AccountCircle />}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyUp={enterKeyHandler}
                        disabled={disableUI}
                    />

                    <BaseTextField
                        placeholder={t('Password')}
                        type="password"
                        startIcon={<Lock />}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyUp={enterKeyHandler}
                        disabled={disableUI}
                    />

                    {showAlert && (
                        <BaseAlert
                            severity={errorMessage ? 'error' : 'success'}
                            message={errorMessage || successMessage || ''}
                        />
                    )}

                    <div className="button-group">
                        <BaseButton
                            className="login-button"
                            startIcon={<HowToReg />}
                            color="secondary"
                            onClick={loginHandler}
                            disabled={disableUI}
                        >
                            {t('Sign In')}
                        </BaseButton>
                        {
                        <BaseButton
                            className="register-button"
                            startIcon={<PersonAddAlt1 />}
                            color="primary"
                            variant="outlined"
                            onClick={() => {}}
                            disabled={disableUI}
                        >
                            {t('Register')}
                        </BaseButton>
                        }
                    </div>
                </div>
                {
                <a
                    href="#"
                    className={`forgot-password-link ${disableUI ? 'disabled-link' : ''}`}
                    onClick={(e) => disableUI && e.preventDefault()}
                >
                    {t('Forgot Password?')}
                </a>
                }
            </BaseCard>
            <BaseDialog
                open={toggleResetDialog}
                dialogTitle={t('Password Expired')}
                confirmButtonText={t('OK')}
                onConfirm={() => {
                    navigate('/reset', { replace: true });
                }}
                cancelButtonText=""
            >
                <Typography sx={{ whiteSpace: 'pre-line' }}>{t('Password Expired Body')}</Typography>
            </BaseDialog>
        </div>
    );
}
